import {
  useCallback, useEffect, useMemo, useRef, useState,
  type KeyboardEvent as ReactKeyboardEvent, type ReactNode,
} from 'react';
import {
  useCreateRobot, useMintEnrollmentToken, useRobots, MORPHOLOGIES,
  type EnrollmentToken, type Morphology,
} from '../../../hooks/useRobots.ts';
import { useVersions } from '../../../hooks/useCatalog.ts';
import { Checkbox, Field, Input } from '../../../components/Field.tsx';
import { Button, IconButton } from '../../../components/Button.tsx';
import { Icon } from '../../../shell/Icon.tsx';
import styles from './NewRobotDialog.module.css';

/* ==========================================================================
   THE DIALOG SHELL, and the register form built on it.

   Every modal in the fleet workspace is `<Dialog>` plus a body. The shell owns
   the four things a dialog gets wrong when each one re-implements it:

     1. FOCUS. Focus enters the dialog on open, cannot leave it by Tab while it
        is open, and returns to whatever opened it on close. A modal that lets
        Tab walk into the fleet table behind it is a modal only for the mouse.
     2. ESCAPE. Always closes, from anywhere inside, including from a text
        field mid-edit. Where a dismissal would throw work away — a typed
        registration, a staged patch, a credential shown once — one shared
        guard (useDismissGuard, below) makes the FIRST press say what will be
        lost and the SECOND press always go through, and the header prints
        `Esc ×2` for as long as that is true.
     3. THE BANDS. A 28px header and a 38px footer on --bg-0, both fixed; only
        the body scrolls. The commit pair can never be scrolled out of reach.
     4. PROVENANCE. The header prints the exact API call the dialog is about to
        make — `POST /api/robots`, `DELETE /api/robots/jetrover-01` — which
        answers "what does this actually do to the fleet" without opening the
        network tab. Every dialog in this family carries it, the irreversible
        one most of all.
   ========================================================================== */

const FOCUSABLE = [
  'a[href]', 'button:not([disabled])', 'input:not([disabled])',
  'select:not([disabled])', 'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function focusables(root: HTMLElement | null): HTMLElement[] {
  if (root === null) return [];
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE))
    .filter((el) => el.offsetParent !== null || el === document.activeElement);
}

export function Dialog({
  title,
  subject,
  endpoint,
  width = 'md',
  labelledBy,
  initialFocus,
  onDismiss,
  dismissHint = 'Esc',
  footLead,
  footEnd,
  children,
}: {
  /** The verb, in the UI face. "Register a robot". */
  title: string;
  /** Which object — a robot id. A machine string, so it renders mono. */
  subject?: ReactNode | undefined;
  /** The call this dialog makes, printed as chrome. `POST /api/robots`. */
  endpoint?: string | undefined;
  width?: 'sm' | 'md' | 'lg' | undefined;
  labelledBy: string;
  /**
   * CSS selector, resolved inside the dialog, for the control focus should
   * land on. Defaults to the first focusable — right for a form, wrong for the
   * enrolment token, where the operator's whole job is the copy button.
   */
  initialFocus?: string | undefined;
  /** Escape, the ✕, and a click on the scrim all arrive here. */
  onDismiss: () => void;
  /** Printed beside the ✕. Override when Escape does something unusual. */
  dismissHint?: string | undefined;
  footLead?: ReactNode | undefined;
  footEnd?: ReactNode | undefined;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  /* Focus in on open, back out on close.

     The subtle half is the way back. Two of these dialogs are opened from an
     overflow menu that closes as it opens them, so by the time the dialog
     unmounts the element that opened it is DETACHED — and focusing a detached
     node silently sends focus to <body>, which strands a keyboard operator on
     a page with no caret and no Escape. So the ancestor chain is recorded at
     open time and walked at close: the first link still in the document gets
     focus, or its first focusable descendant does — for a menu item, that is
     the `⋮` trigger the menu lived in, which is where the operator's hand
     already is.

     The walk stops at <body>: if nothing below it survived, the dialog had no
     opener to speak of and focus is left where the browser puts it, which is
     what a native <dialog> does. Throwing focus to the document's first link
     instead would be a jump, not a restore. */
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const chain: HTMLElement[] = [];
    for (let n = opener; n !== null && n !== document.body; n = n.parentElement) {
      chain.push(n);
    }

    const root = ref.current;
    const wanted = initialFocus === undefined
      ? null
      : root?.querySelector<HTMLElement>(initialFocus) ?? null;
    (wanted ?? focusables(root)[0] ?? root)?.focus();

    return () => {
      for (const node of chain) {
        if (!document.contains(node)) continue;
        if (node.matches(FOCUSABLE)) { node.focus(); return; }
        const inner = node.querySelector<HTMLElement>(FOCUSABLE);
        if (inner !== null) { inner.focus(); return; }
        return;
      }
    };
    // Mount-only: re-running would yank focus out from under the operator on
    // every re-render of a form they are typing into.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onDismiss();
      return;
    }
    if (e.key !== 'Tab') return;
    const items = focusables(ref.current);
    if (items.length === 0) {
      e.preventDefault();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === ref.current)) {
      e.preventDefault();
      last?.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first?.focus();
    }
  };

  return (
    <div
      className={styles.overlay}
      /* mousedown, not click: a selection drag that starts on the token and
         ends on the scrim must not dismiss the dialog. */
      onMouseDown={(e) => { if (e.target === e.currentTarget) onDismiss(); }}
      role="presentation"
    >
      <div
        ref={ref}
        className={styles.dialog}
        data-w={width}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <div className={styles.head}>
          <h2 className={styles.headTitle} id={labelledBy}>{title}</h2>
          {subject !== undefined && <span className={styles.headSubj}>{subject}</span>}
          {endpoint !== undefined && <span className={styles.headMeta}>{endpoint}</span>}
          <span className={styles.headKbd} aria-hidden="true">{dismissHint}</span>
          <IconButton icon="close" label="Close" size="sm" onClick={onDismiss} />
        </div>

        <div className={styles.body}>{children}</div>

        <div className={styles.foot}>
          <span className={styles.footLead}>{footLead}</span>
          <span className={styles.footEnd}>{footEnd}</span>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------------
   DISMISSAL, when dismissing loses something.

   Three of these four dialogs can throw work away on a stray Escape: a
   half-typed registration, a staged patch, and — worst — a credential the
   API will never show again. One rule covers all three: the FIRST dismissal
   arms and says what will be lost, the SECOND always goes through. Escape is
   never disabled, because a modal you cannot leave is the worse defect; it
   just asks once. The dialog header prints `Esc ×2` while the guard is live,
   so the operator learns the cost before they pay it.
   -------------------------------------------------------------------------- */

// The guard belongs beside the shell it guards, not in a fourth file.
// eslint-disable-next-line react-refresh/only-export-components
export function useDismissGuard(atRisk: boolean, onClose: () => void) {
  const [armed, setArmed] = useState(false);
  /* Saving, copying or clearing the form disarms it — an operator who has
     since secured their work must not be asked twice. */
  useEffect(() => { if (!atRisk) setArmed(false); }, [atRisk]);
  const dismiss = useCallback(() => {
    if (!atRisk || armed) { onClose(); return; }
    setArmed(true);
  }, [atRisk, armed, onClose]);
  return { armed: armed && atRisk, dismiss, hint: atRisk ? 'Esc \u00d72' : 'Esc' };
}

/** A titled block inside a dialog body. The eyebrow carries its aside inline. */
export function DialogSection({
  label,
  note,
  tone,
  children,
}: {
  label: string;
  note?: ReactNode | undefined;
  tone?: string | undefined;
  children: ReactNode;
}) {
  return (
    <section className={styles.sec}>
      <div className={styles.secHead}>
        <span className={styles.eyebrow}>{label}</span>
        {note !== undefined && (
          <span className={styles.secNote} {...(tone === undefined ? {} : { 'data-tone': tone })}>
            {note}
          </span>
        )}
      </div>
      {children}
    </section>
  );
}

/* --------------------------------------------------------------------------
   API failure, rendered as words.

   The hooks flatten FastAPI's `{detail}` / `{detail:[{msg,loc}]}` into the
   thrown message already, so most failures arrive as a sentence. What is left
   is the case where the server sent no body at all and the hook fell back to
   `status 422` — a string no operator should ever be shown. Those become the
   plain-English meaning of the code, with the code kept as a dim suffix so it
   is still greppable against a server log.
   -------------------------------------------------------------------------- */

const STATUS_TEXT: Record<string, string> = {
  '400': 'The API rejected the request as malformed.',
  '401': 'Not signed in, or the session expired. Reload to sign in again.',
  '403': 'This account is not permitted to perform that action.',
  '404': 'The API no longer has that robot.',
  '409': 'Something else already holds that identity.',
  '422': 'The API rejected a field but returned no message naming it.',
  '500': 'The API failed while handling the request.',
  '502': 'The API is unreachable from the console (bad gateway).',
  '503': 'The API is not accepting requests right now.',
  '504': 'The API did not answer in time.',
};

export function ApiError({ error, verb }: { error: unknown; verb: string }) {
  if (error === null || error === undefined) return null;
  const raw = (error instanceof Error ? error.message : String(error))
    .replace(/^Error:\s*/, '')
    .trim();
  const code = /^status\s+(\d{3})$/i.exec(raw);
  const text = code
    ? (STATUS_TEXT[code[1] ?? ''] ?? 'The API rejected the request.')
    : raw || 'The request failed before the API answered.';
  return (
    <div className={styles.apiError} role="alert">
      <span className={styles.apiErrorLabel}>{verb} failed</span>
      <span className={styles.apiErrorText}>{text}</span>
      {code && <span className={styles.apiErrorMeta}>HTTP {code[1]}</span>}
    </div>
  );
}

/* --------------------------------------------------------------------------
   Copy — the verb, not an ornament.

   Writing to the clipboard can be refused (an insecure origin, a denied
   permission) and the failure is silent unless we say so, which is intolerable
   for a credential the operator believes they have saved. So the result is
   rendered in the control itself and reported to assistive tech.
   -------------------------------------------------------------------------- */

export function CopyButton({
  value,
  label,
  variant = 'secondary',
  size = 'md',
  id,
  kbd,
  onCopied,
}: {
  value: string;
  label: string;
  variant?: 'primary' | 'secondary' | 'ghost' | undefined;
  size?: 'sm' | 'md' | undefined;
  id?: string | undefined;
  kbd?: string | undefined;
  onCopied?: (() => void) | undefined;
}) {
  const [state, setState] = useState<'idle' | 'done' | 'fail'>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (timer.current !== null) clearTimeout(timer.current); }, []);

  const copy = useCallback(() => {
    const settle = (s: 'done' | 'fail') => {
      setState(s);
      if (timer.current !== null) clearTimeout(timer.current);
      timer.current = setTimeout(() => setState('idle'), 2000);
    };
    navigator.clipboard.writeText(value).then(
      () => { settle('done'); onCopied?.(); },
      () => { settle('fail'); },
    );
  }, [value, onCopied]);

  return (
    <Button
      variant={variant}
      size={size}
      icon={state === 'done' ? 'check' : state === 'fail' ? 'alert-triangle' : 'copy'}
      onClick={copy}
      {...(kbd === undefined ? {} : { kbd })}
      {...(id === undefined ? {} : { id })}
      title={state === 'fail' ? 'The browser refused clipboard access' : label}
    >
      {state === 'done' ? 'Copied' : state === 'fail' ? 'Blocked' : label}
    </Button>
  );
}


/* --------------------------------------------------------------------------
   Segmented choice — an exclusive strip.

   Four or five values the operator has to compare are worth four or five
   visible cells; a <select> hides all but one and tells you nothing about
   which you started from. It is a real radiogroup: arrows move the selection
   AND the focus (roving tabindex), Home/End jump to the ends, and one Tab
   stop covers the whole group.
   -------------------------------------------------------------------------- */

export function Segmented<T extends string>({
  label,
  options,
  value,
  was,
  onChange,
}: {
  label: string;
  options: readonly T[];
  value: T;
  /** The stored value, marked once the form is dirty, so the way back shows. */
  was?: string | undefined;
  onChange: (v: T) => void;
}) {
  const move = (el: HTMLElement, index: number, next: T) => {
    onChange(next);
    el.parentElement
      ?.querySelectorAll<HTMLButtonElement>('[role="radio"]')[index]
      ?.focus();
  };
  return (
    <div className={styles.seg} role="radiogroup" aria-label={label}>
      {options.map((o, i) => (
        <button
          key={o}
          type="button"
          role="radio"
          aria-checked={o === value}
          tabIndex={o === value ? 0 : -1}
          className={styles.segCell}
          data-on={o === value}
          data-was={was !== undefined && o === was && o !== value}
          onClick={() => onChange(o)}
          onKeyDown={(e) => {
            const d = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1
              : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? -1 : 0;
            const index = e.key === 'Home' ? 0
              : e.key === 'End' ? options.length - 1
                : d === 0 ? -1 : (i + d + options.length) % options.length;
            if (index < 0) return;
            e.preventDefault();
            const next = options[index];
            if (next !== undefined) move(e.currentTarget, index, next);
          }}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

/* ==========================================================================
   REGISTER A ROBOT

   `robot_id` is the whole decision: it is the robot's identity for the life of
   the machine, it is immutable once written, it is what the X.509 subject is
   issued against, and the server validates it against a slug regex. So the
   rules are printed as a checklist that resolves live rather than as a
   sentence the operator reads once and a 422 they read afterwards.

   The rest of the form is two fields and a preview of the record that will
   exist when they press Register — including the hardware revision the API
   will pin, which is the part nobody expects.

   AND IT MINTS THE ENROLMENT TOKEN, because registering was never the job.

   PRD-0001 §5 makes provisioning three steps: (1) an operator mints a one-time
   token, (2) it goes to the machine out of band, (3) the agent exchanges it for
   an X.509 client certificate — which is the credential every cloud call the
   robot ever makes is authenticated with. This dialog used to do step 0, write
   the row, and stop. The operator was then sent to the robot's own page, where
   the `Generate token` verb is drawn on the bring-up ladder's first BLOCKED
   rung — and on a robot registered thirty seconds ago the first blocked rung is
   `Bring-up published`, not `Certificate issued`. So the one verb that carries
   provisioning forward was not on screen anywhere in the console, and a fresh
   robot could be registered but never provisioned.

   Minting is therefore part of the act, defaulted on and stated rather than
   implied. It is a separate call and can fail on its own, so the dialog holds
   a `created` id: once the row exists, Register is spent — re-pressing it would
   be a 409 — and the primary verb becomes `Generate token`, retryable until it
   lands or the operator leaves.
   ========================================================================== */

/* The server's rule, verbatim: api/schemas.py `_SLUG_RE`.
   ^[a-z0-9][a-z0-9\-]{1,63}$  — 2–64 chars, first is alphanumeric. */
const RE_CHARS = /^[a-z0-9-]*$/;
const RE_HEAD = /^[a-z0-9]/;

interface Rule {
  label: string;
  state: 'idle' | 'pass' | 'fail';
}

export function NewRobotDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  /**
   * The robot exists. `token` is the one-time enrolment secret when the same
   * act minted one — the caller must show it, because the API returns the raw
   * value exactly once and nothing can read it back.
   */
  onCreated: (robotId: string, token: EnrollmentToken | null) => void;
}) {
  const create = useCreateRobot();
  const fleet = useRobots();
  const [robotId, setRobotId] = useState('');
  const [name, setName] = useState('');
  const [morphology, setMorphology] = useState<Morphology>('rover');
  const [touched, setTouched] = useState(false);
  const [mintOnCreate, setMintOnCreate] = useState(true);
  /* Set the moment POST /api/robots returns 201. From then on the row exists
     whatever happens next, so the dialog can no longer offer to create it. */
  const [created, setCreated] = useState<string | null>(null);

  const id = robotId.trim();
  const taken = useMemo(
    () => new Set((fleet.data ?? []).map((r) => r.robot_id)),
    [fleet.data],
  );

  /* The next free id in this morphology's own numbering, read off the fleet
     rather than invented: `jetrover-01 … jetrover-08` exist, so the suggestion
     is `jetrover-09`. It is a placeholder and a one-click fill, never a
     silently pre-filled field — the operator names the machine. */
  const suggestion = useMemo(() => {
    let n = 1;
    for (;;) {
      const candidate = `${morphology}-${String(n).padStart(2, '0')}`;
      if (!taken.has(candidate)) return candidate;
      n += 1;
    }
  }, [morphology, taken]);

  const rules: Rule[] = [
    {
      label: '2–64 chars',
      state: id === '' ? 'idle' : id.length >= 2 && id.length <= 64 ? 'pass' : 'fail',
    },
    {
      label: 'a–z 0–9 -',
      state: id === '' ? 'idle' : RE_CHARS.test(id) ? 'pass' : 'fail',
    },
    {
      label: 'starts a–z 0–9',
      state: id === '' ? 'idle' : RE_HEAD.test(id) ? 'pass' : 'fail',
    },
    /* Advisory, not authoritative: the server owns uniqueness and answers 409.
       Until the fleet list has loaded this rule stays idle and does NOT gate
       Register — a form that cannot be submitted because a background query is
       slow is a form that looks broken. */
    {
      label: 'unused',
      state: id === '' || !fleet.isSuccess ? 'idle' : taken.has(id) ? 'fail' : 'pass',
    },
  ];
  const broken = rules.filter((r) => r.state === 'fail');
  const valid = id !== '' && broken.length === 0;

  /* What the API will pin. Both create() and a later morphology change resolve
     to the morphology's provisional `001` (pipeline/models/robots.py), so the
     preview reads the catalog for that revision and reports whether it can
     actually bring a robot up — a fresh registration usually cannot, and that
     is worth knowing before the operator mints a token and finds out. */
  const versions = useVersions(morphology);
  const provisional = versions.data?.find((v) => v.version === '001');

  /* Keyed on the typed id, which is the id the row will have. The hook only
     closes over it to build the URL, so it is correct from the first keystroke
     and still correct when the mutation actually runs. */
  const mint = useMintEnrollmentToken(id);
  const busy = create.isPending || mint.isPending;

  const doMint = (rid: string) => {
    mint.mutate(undefined, {
      onSuccess: (token) => onCreated(rid, token),
      /* Deliberately NOT handing the failure onwards as a success. The row
         exists and the token does not; the dialog stays open, says so, and
         keeps the retry — closing here would leave an operator believing they
         had provisioned a robot that cannot authenticate. */
    });
  };

  const submit = () => {
    if (busy) return;
    /* The row already exists — this press is the mint retry, not a second
       registration. */
    if (created !== null) { doMint(created); return; }
    if (!valid) return;
    create.mutate(
      {
        robot_id: id,
        name: name.trim() || undefined,
        morphology,
      },
      {
        onSuccess: (robot) => {
          setCreated(robot.robot_id);
          if (mintOnCreate) doMint(robot.robot_id);
          else onCreated(robot.robot_id, null);
        },
      },
    );
  };

  /* Anything typed is work an Escape would throw away, so the guard arms.
     Once the row exists there is nothing left to discard — the registration is
     already on the server — so the guard disarms and a dismissal reports the
     robot upwards rather than pretending the dialog never happened. */
  const dirty = id !== '' || name.trim() !== '' || morphology !== 'rover';
  const leave = useCallback(
    () => { if (created !== null) onCreated(created, null); else onClose(); },
    [created, onCreated, onClose],
  );
  const guard = useDismissGuard(created === null && dirty && !busy, leave);

  return (
    <Dialog
      title="Register a robot"
      /* The chrome names every call the commit pair will make, in order — a
         dialog that mints a credential must not advertise only the harmless
         half of what it does. */
      endpoint={
        created !== null
          ? `POST /api/robots/${created}/enrollment-token`
          : mintOnCreate
            ? 'POST /api/robots · POST /api/robots/{id}/enrollment-token'
            : 'POST /api/robots'
      }
      width="md"
      labelledBy="new-robot-title"
      initialFocus="#new-robot-id"
      onDismiss={guard.dismiss}
      dismissHint={guard.hint}
      footLead={
        <span
          className={styles.live}
          aria-live="polite"
          {...(guard.armed ? { 'data-tone': 'warn' }
            : create.isError || mint.isError || (broken.length > 0 && touched)
              ? { 'data-tone': 'error' }
              : {})}
        >
          {guard.armed
            ? 'Dismiss again to discard this registration.'
            : create.isPending
              ? 'Registering…'
              : mint.isPending
                ? 'Minting the enrolment token…'
                /* The half-done state, said plainly. The row is on the server
                   and the credential is not, which is neither success nor
                   failure and must not be rendered as either. */
                : mint.isError && created !== null
                  ? `${created} is registered — the token was not minted`
                  : create.isError
                    ? 'Not registered — the API refused'
                    : broken.length > 0 && touched
                      ? `robot_id — ${broken.length} rule${broken.length === 1 ? '' : 's'} unmet`
                      : valid
                        ? mintOnCreate
                          ? `Creates ${id} and mints its enrolment token`
                          : `Creates ${id}`
                        : ''}
        </span>
      }
      footEnd={
        <>
          <Button onClick={guard.dismiss} disabled={busy}>
            {created !== null ? 'Done' : 'Cancel'}
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={busy || (created === null && !valid)}
            kbd="⌘↵"
            title={
              created !== null
                ? `POST /api/robots/${created}/enrollment-token — retry the mint`
                : valid
                  ? `POST /api/robots — creates ${id}`
                  : 'robot_id is not valid yet'
            }
          >
            {created !== null ? 'Generate token' : mintOnCreate ? 'Register & provision' : 'Register'}
          </Button>
        </>
      }
    >
      <form
        className={styles.form}
        onSubmit={(e) => { e.preventDefault(); submit(); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); }
        }}
      >
        {/* ONCE THE ROW EXISTS THE FORM IS NO LONGER A PROPOSAL, so it stops
            being rendered as one. Left in place it turned on the operator the
            moment the mint failed: the id it had just created came back invalid
            (`✕ unused`), the field went red, `CREATES` read `blocked`, and the
            preview struck the id through as "already registered" — the console
            arguing against a registration it had itself just succeeded at. The
            only live question at that point is the credential. */}
        {created !== null ? (
          <DialogSection label="Registered" note="on the server">
            <dl className={styles.spec}>
              <dt>robot_id</dt>
              <dd>{created}</dd>
              <dt>morphology</dt>
              <dd>{morphology}</dd>
              <dt>revision</dt>
              <dd><span className={styles.specNote}>001 provisional</span></dd>
              <dt>liveness</dt>
              <dd>
                provisioning
                <span className={styles.specNote}>until the robot enrols</span>
              </dd>
            </dl>
            <p className={styles.note}>
              The record exists and cannot be written twice. What is missing is
              the credential — until a token is minted and exchanged for a
              certificate, <code>{created}</code> cannot authenticate a single
              call.
            </p>
          </DialogSection>
        ) : (
          <>
        <DialogSection
          label="Identity"
          note={
            id === ''
              ? (
                <button
                  type="button"
                  className={styles.linkNote}
                  onClick={() => { setRobotId(suggestion); setTouched(true); }}
                  title={`Fill robot_id with ${suggestion} — the lowest unused number in this morphology`}
                >
                  next free {suggestion}
                </button>
              )
              : `${id.length}/64`
          }
        >
          <Field label="Robot ID" htmlFor="new-robot-id" required>
            <Input
              id="new-robot-id"
              mono
              value={robotId}
              /* Editing the field clears the previous attempt's error. A 409
                 about `rover-12` printed under a field that now reads
                 `rover-13` is worse than no error at all. */
              onChange={(e) => {
                setRobotId(e.target.value);
                setTouched(true);
                if (create.isError) create.reset();
              }}
              onBlur={() => setTouched(true)}
              placeholder={suggestion}
              spellCheck={false}
              autoComplete="off"
              invalid={touched && broken.length > 0}
              aria-describedby="new-robot-id-rules"
            />
          </Field>
          {/* The server's slug rule, resolving as you type. Each rule carries a
              glyph as well as a colour, so the state survives a monochrome
              screenshot and a colour-blind operator. */}
          <ul className={styles.rules} id="new-robot-id-rules">
            {rules.map((r) => (
              <li key={r.label} className={styles.rule} data-state={r.state}>
                <span className={styles.ruleMark} aria-hidden="true">
                  {r.state === 'pass' && <Icon name="check" size={10} />}
                  {r.state === 'fail' && <Icon name="close" size={10} />}
                </span>
                {r.label}
              </li>
            ))}
          </ul>
          {taken.has(id) && (
            <p className={styles.note}>
              <code>{id}</code> is already registered. An id is permanent — the API
              has no rename.
            </p>
          )}

          <Field label="Name" htmlFor="new-robot-name" optional>
            <Input
              id="new-robot-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="the label operators read — e.g. Pathfinder 09"
              autoComplete="off"
            />
          </Field>
        </DialogSection>

        <DialogSection
          label="Morphology"
          note={
            versions.data === undefined
              ? undefined
              : `${versions.data.length} revisions · ${versions.data.filter((v) => v.bringup_ready).length} bring-up ready`
          }
        >
          <Segmented
            label="Morphology"
            options={MORPHOLOGIES}
            value={morphology}
            onChange={setMorphology}
          />
        </DialogSection>

        <DialogSection
          label="Provisioning"
          note={mintOnCreate ? 'one-time token' : 'registration only'}
        >
          <Checkbox
            id="new-robot-mint"
            checked={mintOnCreate}
            disabled={created !== null}
            onChange={(e) => { setMintOnCreate(e.currentTarget.checked); }}
            label="Mint the enrolment token now"
          />
          <p className={styles.note}>
            {mintOnCreate
              ? `The token is shown once, here, and never again — the server keeps
                 only its hash. It expires unused, is revocable, and can be minted
                 again from the robot's own page.`
              : `Without a token the robot has no way to enrol, so it can never
                 obtain the certificate it authenticates every call with. You can
                 mint one later from the robot's own page.`}
          </p>
        </DialogSection>

        <DialogSection
          label="Creates"
          note={id !== '' && broken.length > 0 ? 'blocked' : 'registry record only'}
          {...(id !== '' && broken.length > 0 ? { tone: 'error' } : {})}
        >
          <dl className={styles.spec}>
            {/* The preview must not show a record that cannot exist. An id the
                server would reject is printed struck through with the reason,
                not rendered as though Register would create it. */}
            <dt>robot_id</dt>
            <dd {...(id !== '' && broken.length > 0 ? { 'data-tone': 'error' } : {})}>
              <span className={id !== '' && broken.length > 0 ? styles.specVoid : undefined}>
                {id === '' ? '—' : id}
              </span>
              {id !== '' && broken.length > 0 && (
                <span className={styles.specNote}>
                  {taken.has(id) ? 'already registered' : 'not a valid slug'}
                </span>
              )}
            </dd>
            <dt>morphology</dt>
            <dd>{morphology}</dd>
            <dt>revision</dt>
            <dd {...(provisional?.bringup_ready === true ? {} : { 'data-tone': 'warn' })}>
              001
              <span className={styles.specNote}>
                {versions.isLoading
                  ? 'reading catalog…'
                  : provisional === undefined
                    ? 'provisional — created on registration'
                    : provisional.bringup_ready
                      ? 'provisional — bring-up ready'
                      : 'provisional — no bring-up script'}
              </span>
            </dd>
            <dt>liveness</dt>
            <dd>
              provisioning
              <span className={styles.specNote}>until the robot enrols</span>
            </dd>
          </dl>
          <p className={styles.note}>
            Neither step reaches the robot. Registration writes the record the
            machine will authenticate against; the token is carried to it out of
            band, and the agent exchanges it for the certificate itself.
          </p>
        </DialogSection>

        {create.isError && <ApiError error={create.error} verb="Register" />}
          </>
        )}
        {mint.isError && <ApiError error={mint.error} verb="Generate token" />}
      </form>
    </Dialog>
  );
}
